import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';

const categoryIcons: Record<string, string> = {
  'Shoes': '👟',
  'Hoodies / Sweaters': '🧶',
  'T-Shirts': '👕',
  'Jackets': '🧥',
  'Pants / Shorts': '👖',
  'Headwear': '🧢',
  'Sets': '👔',
  'Underwear / Underpants': '🩳',
  'Jersey': '🏈',
  'Accessories': '💎',
  'Bags': '👜',
  'Belts': '🪢',
  'Perfumes': '✨',
  'Other Stuff': '🎁',
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [desktopDropdown, setDesktopDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const desktopDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (desktopDropdownRef.current && !desktopDropdownRef.current.contains(e.target as Node)) {
        setDesktopDropdown(false);
      }
    };
    if (desktopDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [desktopDropdown]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 cursor-pointer"
          >
            <span className="font-heading text-xl md:text-2xl font-bold text-primary-800 whitespace-nowrap">
              Superbuy Spreadsheet
            </span>
          </button>

          <div className="hidden md:flex items-center gap-6" ref={desktopDropdownRef}>
            <div className="relative">
              <button
                onClick={() => setDesktopDropdown(!desktopDropdown)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${desktopDropdown ? 'rotate-180' : ''}`}></i>
              </button>

              {desktopDropdown && (
                <div className="absolute top-full mt-2 left-0 w-[520px] bg-background-50 rounded-2xl border border-background-200/60 shadow-[0_8px_40px_rgba(0,0,0,0.08)] p-4 z-50 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors duration-150 text-sm text-foreground-700 hover:text-foreground-900 cursor-pointer"
                      onClick={() => setDesktopDropdown(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                        <i className="ri-arrow-right-up-line text-xs"></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-800 text-background-50 text-sm font-medium hover:bg-primary-900 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Superbuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 cursor-pointer"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`text-lg transition-transform duration-200 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-black/20 backdrop-blur-sm z-40"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 z-50 bg-background-50 overflow-y-auto animate-slide-down">
            <div className="px-5 py-6">
              <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-[0.2em] mb-4 px-3">
                Product Categories
              </p>

              <div className="grid grid-cols-2 gap-1.5 mb-6">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 active:bg-background-200 transition-colors text-sm text-foreground-700 font-medium cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-base flex-shrink-0">
                      {categoryIcons[cat.name] || '📦'}
                    </span>
                    <span className="leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>

              <div className="border-t border-background-200/60 pt-5 flex flex-col gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-primary-800 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-bag-line"></i>
                  Superbuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-flashlight-line"></i>
                  Buy It Now
                </a>
                <button
                  onClick={() => {
                    navigate('/article/superbuy-spreadsheet-guide');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border-2 border-background-200 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-open-line"></i>
                  Read Our Guide
                </button>
              </div>

              <p className="text-center text-[10px] text-foreground-400 mt-6">
                Independent Educational Resource
              </p>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}